import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../context/Translator";
import { Link, useParams } from "react-router-dom";
import { Row, Col, Card, Badge, Button, Alert, Modal } from "react-bootstrap";
import PageLayout from "../../layouts/PageLayout";
import gastosData from "../../assets/data/gastos.json";

export default function GastoDetalleViewPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const [gasto, setGasto] = useState(null);
    const [showImageModal, setShowImageModal] = useState(false);

    useEffect(() => {
        // Buscar el gasto por ID
        const gastoEncontrado = gastosData.tbody.find(item => item.id === id);
        setGasto(gastoEncontrado);
    }, [id]);

    if (!gasto) {
        return (
            <PageLayout>
                <Alert variant="warning">Gasto no encontrado</Alert>
            </PageLayout>
        );
    }

    const formatCurrency = (amount) => {
        const numAmount = typeof amount === 'string' ? 
            parseInt(amount.replace(/\./g, '')) : amount;
        
        return new Intl.NumberFormat('es-CL', {
            style: 'currency',
            currency: 'CLP',
            minimumFractionDigits: 0
        }).format(numAmount);
    };

    const getStatusBadge = (status, estado_class) => {
        const variants = {
            success: 'success',
            warning: 'warning', 
            danger: 'danger',
            info: 'info'
        };
        return <Badge bg={variants[estado_class] || 'secondary'} className="fs-6">{status}</Badge>;
    };

    const getCategoryColor = (category) => {
        const colors = {
            'Gastos Oficina': '#dc3545',
            'Edmundo Neira': '#0dcaf0',
            'Operativa/Terreno': '#198754',
            'Egresos Operativos': '#ffc107'
        };
        return colors[category] || '#6c757d';
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card">
                        <div className='mc-breadcrumb'>
                            <h3 className="mc-breadcrumb-title">Detalle del Gasto</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item"><Link to='#' className="mc-breadcrumb-link">{t('home')}</Link></li>
                                <li className="mc-breadcrumb-item"><Link to='/listado-gastos' className="mc-breadcrumb-link">Gastos</Link></li>
                                <li className="mc-breadcrumb-item">Ver Gasto</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                {/* Información principal */}
                <Col xl={8}>
                    <Card className="mc-card">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h4>Información del Gasto</h4>
                            <div>
                                {getStatusBadge(gasto.status, gasto.estado_class)}
                                <span className="badge bg-light text-dark ms-2">{gasto.id}</span>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={12} className="mb-4">
                                    <div className="border-start border-4 ps-3" 
                                         style={{ borderColor: `${getCategoryColor(gasto.category)} !important` }}>
                                        <h5 className="mb-1">{gasto.heading}</h5>
                                        <p className="text-muted mb-0">{gasto.describe}</p>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Categoría</label>
                                        <div className="d-flex align-items-center">
                                            <div 
                                                className="me-2" 
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    backgroundColor: getCategoryColor(gasto.category),
                                                    borderRadius: '3px'
                                                }}
                                            ></div>
                                            <strong>{gasto.category}</strong>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Tipo de Gasto</label>
                                        <div><strong>{gasto.tipo_gasto}</strong></div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Monto</label>
                                        <div>
                                            <h4 className="mb-0" style={{ color: getCategoryColor(gasto.category) }}>
                                                {formatCurrency(gasto.comuna)}
                                            </h4>
                                        </div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Fondo Asignado</label>
                                        <div><strong>{gasto.fondo}</strong></div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Fecha del Gasto</label>
                                        <div><strong>{new Date(gasto.brand).toLocaleDateString('es-CL')}</strong></div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Fecha de Ingreso</label>
                                        <div><strong>{new Date(gasto.fecha_ingreso).toLocaleDateString('es-CL')}</strong></div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Responsable</label>
                                        <div><strong>{gasto.responsable}</strong></div>
                                    </div>
                                </Col>

                                <Col md={6}>
                                    <div className="mb-3">
                                        <label className="form-label text-muted">Estado</label>
                                        <div>{getStatusBadge(gasto.status, gasto.estado_class)}</div>
                                    </div>
                                </Col>

                                {gasto.observaciones && (
                                    <Col md={12}>
                                        <div className="mb-3">
                                            <label className="form-label text-muted">Observaciones</label>
                                            <div className="p-3 bg-light rounded">
                                                {gasto.observaciones}
                                            </div>
                                        </div>
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Panel lateral */}
                <Col xl={4}>
                    {/* Documento adjunto */}
                    <Card className="mc-card mb-4">
                        <Card.Header>
                            <h5>Documento Adjunto</h5>
                        </Card.Header>
                        <Card.Body>
                            {gasto.documento ? (
                                <div className="text-center">
                                    <div className="mb-3">
                                        <i className="material-icons" style={{ fontSize: '48px', color: '#dc3545' }}>
                                            picture_as_pdf
                                        </i>
                                    </div>
                                    <h6>{gasto.documento}</h6>
                                    <div className="d-grid gap-2">
                                        <Button variant="primary" onClick={() => setShowImageModal(true)}>
                                            <i className="material-icons me-2">visibility</i>
                                            Ver Documento
                                        </Button>
                                        <Button variant="outline-primary">
                                            <i className="material-icons me-2">download</i>
                                            Descargar
                                        </Button>
                                    </div>
                                </div>
                            ) : (
                                <div className="text-center text-muted">
                                    <i className="material-icons mb-2" style={{ fontSize: '48px' }}>
                                        insert_drive_file
                                    </i>
                                    <p>Sin documento adjunto</p>
                                </div>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Acciones */}
                    <Card className="mc-card">
                        <Card.Header>
                            <h5>Acciones</h5>
                        </Card.Header>
                        <Card.Body>
                            <div className="d-grid gap-2">
                                {gasto.status === 'Pendiente' && (
                                    <Link to={`/gastos/validar/${gasto.id}`}>
                                        <Button variant="success" className="w-100">
                                            <i className="material-icons me-2">check_circle</i>
                                            Validar Gasto
                                        </Button>
                                    </Link>
                                )}
                                
                                <Link to={`/gastos/editar/${gasto.id}`}>
                                    <Button variant="warning" className="w-100">
                                        <i className="material-icons me-2">edit</i>
                                        Editar Gasto
                                    </Button>
                                </Link>
                                
                                <Button variant="info" className="w-100">
                                    <i className="material-icons me-2">print</i>
                                    Imprimir
                                </Button>
                                
                                <Button variant="outline-secondary" className="w-100">
                                    <i className="material-icons me-2">share</i>
                                    Compartir
                                </Button>
                                
                                <hr />
                                
                                <Link to="/listado-gastos">
                                    <Button variant="secondary" className="w-100">
                                        <i className="material-icons me-2">arrow_back</i>
                                        Volver al Listado
                                    </Button>
                                </Link>
                            </div>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            {/* Modal para ver imagen */}
            <Modal show={showImageModal} onHide={() => setShowImageModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Documento: {gasto.documento}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center">
                    <div className="p-4">
                        <i className="material-icons mb-3" style={{ fontSize: '96px', color: '#dc3545' }}>
                            picture_as_pdf
                        </i>
                        <h5>Vista previa del documento</h5>
                        <p className="text-muted">
                            El documento se mostraría aquí en una implementación completa
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowImageModal(false)}>
                        Cerrar
                    </Button>
                    <Button variant="primary">
                        Descargar
                    </Button>
                </Modal.Footer>
            </Modal>
        </PageLayout>
    );
}
